import { DestroyRef, Injectable, inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { StorageService } from '@comparit/infrastructure';
import { isEqual } from '@comparit/core';
import {
  CalculatorForm,
  CalculatorInput,
  SchutzOptimierenForm,
  SchutzOptimierenValue,
} from '@comparit/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
} from 'rxjs';
import { AttributDataType, FormsKeys, HausratBerechnungvorgaben, StepsForms } from '../interfaces';
import {
  VertragsdatenFormCreator,
  VertragsdatenStepForms,
} from './vertragsdaten/vertragsdaten-form.service';
import { mapVertragsdatenToDto } from './vertragsdaten/vertragsdaten-mapper';
import {
  GebaeudedatenFormCreator,
  GebaeudedatenStepForms,
} from './gebaeudedaten/gebaeudedaten-form.service';
import { mapGebaeudedatenToDto } from './gebaeudedaten/gebaeudedaten-mapper';
import { ZurPersonFormCreator, ZurPersonStepForms } from './zur-person/zur-person-form.service';
import { mapZurPersonToDto } from './zur-person/zur-person-mapper';
import {
  VersicherungsdatenFormCreator,
  VersicherungsdatenStepForms,
} from './versicherungsdaten/versicherungsdaten-form.service';
import { mapVersicherungsdatenToDto } from './versicherungsdaten/versicherungsdaten-mapper';

@Injectable({
  providedIn: 'root',
})
export class FormsService {
  private readonly _storage = inject(StorageService);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sync();
  }

  public vertragsdatenFormCreator = new VertragsdatenFormCreator(
    this._fb,
    this._storage.getData<VertragsdatenStepForms>(FormsKeys.vertragsdaten)
  );

  public gebaeudedatenFormCreator = new GebaeudedatenFormCreator(
    this._fb,
    this._storage.getData<GebaeudedatenStepForms>(FormsKeys.gebaeudedaten)
  );

  public zurPersonFormCreator = new ZurPersonFormCreator(
    this._fb,
    this._storage.getData<ZurPersonStepForms>(FormsKeys.zurPerson)
  );

  public versicherungsdatenFormCreator = new VersicherungsdatenFormCreator(
    this._fb,
    this._storage.getData<VersicherungsdatenStepForms>(FormsKeys.versicherungsdaten)
  );

  /** Its controls come from the catalogue — see SchutzOptimierenKatalogService. */
  public schutzOptimierenForm = new SchutzOptimierenForm(
    this._fb,
    AttributDataType.Boolean,
    this._storage.getData<SchutzOptimierenValue>(FormsKeys.schutzOptimieren)
  );

  /** Compare and sort state of the results page; the shared lib reads it through ResultsService. */
  public calculatorForm = new CalculatorForm(
    this._fb,
    this._storage.getData<CalculatorInput>(FormsKeys.calculator)
  );

  allFgs: StepsForms = {
    ...this.vertragsdatenFormCreator.stepsCtrls,
    ...this.gebaeudedatenFormCreator.stepsCtrls,
    ...this.zurPersonFormCreator.stepsCtrls,
    ...this.versicherungsdatenFormCreator.stepsCtrls,
    step11: this.schutzOptimierenForm.fg,
  };

  /**
   * Everything the Sparte needs except the Schutz-optimieren answers. This is also the body of the
   * catalogue request, which must not re-run when a tile is toggled.
   */
  vorgabenOhneSchutz$: Observable<HausratBerechnungvorgaben> = combineLatest([
    this.vertragsdatenFormCreator.fgValue$,
    this.gebaeudedatenFormCreator.fgValue$,
    this.zurPersonFormCreator.fgValue$,
    this.versicherungsdatenFormCreator.fgValue$,
  ]).pipe(
    map(([vertragsdaten, gebaeudedaten, zurPerson, versicherungsdaten]) => ({
      vertragsdaten: mapVertragsdatenToDto(vertragsdaten),
      gebaeudedaten: mapGebaeudedatenToDto(gebaeudedaten),
      personlicheAngaben: mapZurPersonToDto(zurPerson),
      deckungsumfang: mapVersicherungsdatenToDto(versicherungsdaten),
    })),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** The funnel as the Sparte wants it. */
  berechnungFormularValue$: Observable<HausratBerechnungvorgaben> = combineLatest([
    this.vorgabenOhneSchutz$,
    this.schutzOptimierenForm.dynamischeAngaben$,
  ]).pipe(
    map(([vorgaben, dynamischeAngaben]) => ({
      ...vorgaben,
      schutzOptimieren: { dynamischeAngaben },
    })),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private sync() {
    this.syncHelper(FormsKeys.vertragsdaten, this.vertragsdatenFormCreator.fgValueChanges$);
    this.syncHelper(FormsKeys.gebaeudedaten, this.gebaeudedatenFormCreator.fgValueChanges$);
    this.syncHelper(FormsKeys.zurPerson, this.zurPersonFormCreator.fgValueChanges$);
    this.syncHelper(
      FormsKeys.versicherungsdaten,
      this.versicherungsdatenFormCreator.fgValueChanges$
    );
    this.syncHelper(FormsKeys.schutzOptimieren, this.schutzOptimierenForm.fgValueChanges$);
    this.syncHelper(FormsKeys.calculator, this.calculatorForm.form.fgValueChanges$);
  }

  private syncHelper(taskKey: string, fg: Observable<unknown>) {
    fg.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100)).subscribe(value => {
      this._storage.save(taskKey, value);
    });
  }

  isCalculationFormInvalid(): boolean {
    return Object.values(this.allFgs).some(fg => fg.invalid);
  }

  resetAll() {
    this.vertragsdatenFormCreator._fg.setValue(this.vertragsdatenFormCreator.fgDefaultValue);
    this.gebaeudedatenFormCreator._fg.setValue(this.gebaeudedatenFormCreator.fgDefaultValue);
    this.zurPersonFormCreator._fg.setValue(this.zurPersonFormCreator.fgDefaultValue);
    this.versicherungsdatenFormCreator.reset();
    this.schutzOptimierenForm.reset();
    this.calculatorForm.reset();
  }
}
